/* eslint-disable react-native/no-inline-styles */

import React, { useLayoutEffect, useRef, useState } from 'react';
import { LayoutChangeEvent, View, useWindowDimensions } from 'react-native';
import {
  DocketItemStatus,
  DocketStatus,
  LayoutStrategy,
  OtherDocketItems,
} from '@oolio-group/domain';
import { useScale } from '../../../hooks/useScale';
import { useSession } from '../../../hooks/useSession';
import { useDockets } from '../../../hooks/useDockets';
import { useOrderTypes } from '../../../hooks/useOrderTypes';
import { useSalesChannels } from '../../../hooks/useSalesChannels';
import DocketItem from '../../../component/DocketItem/DocketItem';
import { PaginatedDocketItems } from '../../../utils/layoutHelper';
import MainScreenStyles from '../MainScreen.styles';
import { DOCKET_WIDTH } from '../../../common/constants';
import {
  DocketDisplayItem,
  DocketDisplayItemType,
  DocketDetailsProps,
  DocketDisplayOrderItem,
} from '../../../common/types';
import { bulkSelectVar, selectedDocketVar } from '../../../state/apolloVars';

export interface DocketsGridProps {
  displayDocketItems: PaginatedDocketItems[];
  status: DocketStatus;
  currentPage: number;
  enablePrint?: boolean;
  setColumnsHeight?: (height: number) => void;
  columnsHeight?: number;
  onToggleDocketDetails: (a: DocketDetailsProps) => void;
  setDocketDetails: (a: DocketDetailsProps) => void;
  selectedDocket?: string;
}

const DocketsGrid: React.FC<DocketsGridProps> = ({
  displayDocketItems,
  status,
  currentPage,
  enablePrint,
  setColumnsHeight,
  columnsHeight,
  onToggleDocketDetails,
  setDocketDetails,
  selectedDocket,
}) => {
  const {
    bumpDocketWithStatus,
    recallDocket,
    bumpDocketItem,
    recallDocketItem,
    serveDocketItem,
    reBumpDocketItem,
    printKitchenDocket,
  } = useDockets();
  const { scale } = useScale();
  const { session } = useSession();
  const styles = MainScreenStyles();
  const { orderTypes } = useOrderTypes();
  const { width } = useWindowDimensions();
  const { salesChannels } = useSalesChannels();

  const [itemWidth, setItemWidth] = useState<number>(0);
  const heightRef = useRef<any>(undefined);
  const isBulkMode = bulkSelectVar();
  const selectedDocketId = Object.keys(selectedDocketVar())?.[0];
  const isSingleSelectedMode = !isBulkMode && selectedDocketId;

  useLayoutEffect(() => {
    const columnsPossible = Math.min(
      Math.floor(width / scale(DOCKET_WIDTH)),
      session?.kitchenDisplay?.layoutStrategy?.columnsOnScreen ?? 8,
    );
    setItemWidth(Math.floor(width / columnsPossible - scale(12)));
  }, [scale, width, session?.kitchenDisplay?.layoutStrategy?.columnsOnScreen]);

  const handleOnPressDocketHeader = async (
    docketId: string,
    otherItems: OtherDocketItems[] | undefined,
  ) => {
    if (status == DocketStatus.CREATED)
      return bumpDocketWithStatus(
        docketId,
        session?.rebumpToggle
          ? DocketItemStatus.REBUMPED
          : DocketItemStatus.BUMPED,
        otherItems,
      );
    else return recallDocket(docketId);
  };

  const handleOnPressDocketItem = async (
    docketItemId: string,
    itemStatus: DocketItemStatus,
  ) => {
    if (
      itemStatus == DocketItemStatus.CREATED ||
      itemStatus == DocketItemStatus.RECALLED
    ) {
      await bumpDocketItem(docketItemId);
    } else if (
      status == DocketStatus.CREATED &&
      itemStatus == DocketItemStatus.BUMPED
    ) {
      session?.rebumpToggle
        ? await reBumpDocketItem(docketItemId)
        : await serveDocketItem(docketItemId);
    } else if (
      status == DocketStatus.CREATED &&
      itemStatus == DocketItemStatus.REBUMPED
    ) {
      await serveDocketItem(docketItemId);
    } else {
      await recallDocketItem(docketItemId);
    }
  };
  const onLayout = (event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    if (!columnsHeight || columnsHeight <= 0) {
      setColumnsHeight?.(height);
    }
  };
  const getRowItems = (items: DocketDisplayItem[], rowPosition: string) => {
    if (
      session?.kitchenDisplay?.layoutStrategy?.strategy !==
      LayoutStrategy.DOUBLE_ROW
    ) {
      if (rowPosition === 'top') {
        return items;
      }
      return [];
    }

    return items.filter(
      item =>
        (item.type === DocketDisplayItemType.DOCKET_HEADER &&
          item?.position?.rowPosition === rowPosition) ||
        (item.type !== DocketDisplayItemType.DOCKET_HEADER &&
          item?.position?.parentRowPosition === rowPosition),
    );
  };

  return (
    <View style={styles.grid} ref={heightRef} onLayout={onLayout}>
      {isSingleSelectedMode && <View style={styles.overlay} />}
      {displayDocketItems[currentPage]?.columns.map((col, index) => {
        const isDoubleRow =
          session?.kitchenDisplay?.layoutStrategy?.strategy ===
          LayoutStrategy.DOUBLE_ROW;

        // For FREE_FLOW, put all items in the top row
        const topRowItems = !isDoubleRow
          ? col.items
          : getRowItems(col.items, 'top');
        const bottomRowItems = !isDoubleRow
          ? []
          : getRowItems(col.items, 'bottom');

        return (
          <View key={index} style={styles.gridColumn}>
            <View
              style={[
                styles.rowContainer,
                {
                  height: !isDoubleRow
                    ? columnsHeight || 'auto'
                    : columnsHeight
                    ? columnsHeight / 2
                    : 'auto',
                },
              ]}
            >
              {topRowItems.map((item, i) => {
                const docketDisplayItem = item as DocketDisplayOrderItem;
                const docketId =
                  docketDisplayItem?.docketId || docketDisplayItem?.id;

                return (
                  <View
                    key={`top-${i}`}
                    style={[
                      styles.item,
                      {
                        width: itemWidth,
                      },
                    ]}
                  >
                    {isSingleSelectedMode && selectedDocketId !== docketId && (
                      <View style={styles.overlay} />
                    )}
                    <DocketItem
                      key={`top-${item.name}-${i}`}
                      item={item}
                      status={status}
                      orderTypes={orderTypes}
                      salesChannels={salesChannels}
                      onPressDocketHeader={handleOnPressDocketHeader}
                      onPressDocketItem={handleOnPressDocketItem}
                      onToggleDocketDetails={onToggleDocketDetails}
                      setDocketDetails={setDocketDetails}
                      selectedDocket={selectedDocket}
                      enablePrint={enablePrint}
                      printKitchenDocket={printKitchenDocket}
                    />
                  </View>
                );
              })}
            </View>
            {isDoubleRow && bottomRowItems.length > 0 && (
              <View
                style={[
                  styles.rowContainer,
                  {
                    height: columnsHeight ? columnsHeight / 2 : 'auto',
                    maxHeight: columnsHeight ? columnsHeight / 2 : undefined,
                    overflow: 'hidden',
                  },
                ]}
              >
                {bottomRowItems.map((item, i) => {
                  const docketDisplayItem = item as DocketDisplayOrderItem;
                  const docketId =
                    docketDisplayItem?.docketId || docketDisplayItem?.id;
                  return (
                    <View
                      key={`bottom-${i}`}
                      style={[
                        styles.item,
                        {
                          width: itemWidth,
                        },
                      ]}
                    >
                      {isSingleSelectedMode &&
                        selectedDocketId !== docketId && (
                          <View style={styles.overlay} />
                        )}
                      <DocketItem
                        key={`bottom-${item.name}-${i}`}
                        item={item}
                        status={status}
                        orderTypes={orderTypes}
                        salesChannels={salesChannels}
                        onPressDocketHeader={handleOnPressDocketHeader}
                        onPressDocketItem={handleOnPressDocketItem}
                        onToggleDocketDetails={onToggleDocketDetails}
                        setDocketDetails={setDocketDetails}
                        selectedDocket={selectedDocket}
                        enablePrint={enablePrint}
                        printKitchenDocket={printKitchenDocket}
                      />
                    </View>
                  );
                })}
              </View>
            )}
          </View>
        );
      })}
    </View>
  );
};

export default DocketsGrid;
